// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import Clock from '../images/clock.svg'
import { useContext, useEffect, useState } from 'react';
import { useGetApi } from '../data/hooks/api';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import Note from '../images/journal-text.svg'
import * as Types from "../data/utils/types";
import "../styles/CaseBox.css"
import DOMPurify from 'dompurify';
import Loading from './LinearLoading';
import { Box, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { PermissionContext } from './Authorize';
import { useInlineAttachmentImages } from '../data/hooks/useInlineAttachmentImages';

type MyComponentProps = {
   caseId: string | undefined;
   worknoteRsp: any;
}

export const CaseBox = (propContent: MyComponentProps) => {

   const [offset, setOffset] = useState(0);
   const [endOfComments, setEndOfComments] = useState(false);
   const [allComments, setAllComments] = useState<Types.caseCommentDetails[]>([]);

   // Initialize with empty URL
   const { data, loading, error, getApiData } = useGetApi<{ total: number; comments: Types.caseCommentDetails[] }>({
      url: "",
      headers: { "accept": "application/json" }
   });

   // Function to get comments with specific offset
   const fetchComments = (currentOffset: number) => {
      const url = RESOURCE_SERVER_URLS[0] + `/cases/${propContent.caseId}/comments-and-worknotes?offset=${currentOffset}&limit=10`;
      getApiData(url);
   };

   // Load initial data and handle worknote updates
   useEffect(() => {
      if (propContent.caseId) {
         setOffset(0);
         setEndOfComments(false);
         setAllComments([]);
         fetchComments(0);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [propContent.caseId, propContent.worknoteRsp]);

   // Create a unique key for a comment using timestamp and user
   const getCommentKey = (comment: Types.caseCommentDetails) => {
      return `${comment.createdOn}_${comment.createdBy}_${comment.type}`;
   };

   // Handle data updates
   useEffect(() => {
      if (data?.comments) {
         const { comments, total } = data;
         
         if (offset === 0) {
            // For initial load or case change, replace all comments
            setAllComments(comments);
            // Hide "Show More" if we've loaded all comments
            setEndOfComments(comments.length >= total);
         } else if (comments.length > 0) {
            // Only append if we got new data
            setAllComments(prev => {
               // Create a Set of existing comment keys for O(1) lookup
               const existingKeys = new Set(prev.map(getCommentKey));
               
               // Filter out comments we already have using the composite key
               const newComments = comments.filter((comment: Types.caseCommentDetails) => 
                  !existingKeys.has(getCommentKey(comment))
               );
               
               // Hide "Show More" if we've loaded all comments
               setEndOfComments(prev.length + newComments.length >= total);
               
               return [...prev, ...newComments];
            });
         } else {
            // If we got no new comments, we're at the end
            setEndOfComments(true);
         }
      }
   }, [data, offset]);

   const nextComments = () => {
      const newOffset = offset + 10;
      setOffset(newOffset);
      fetchComments(newOffset);
   };

   const getTimeDifference = (createdOn: string) => {
      const createdDate = new Date(createdOn + "Z");
      const now = new Date();

      const differenceInMilliseconds = now.getTime() - createdDate.getTime();
      const differenceInMinutes = Math.floor(differenceInMilliseconds / 60000);
      const differenceInHours = Math.floor(differenceInMinutes / 60);
      const differenceInDays = Math.floor(differenceInHours / 24);
      const differenceInMonths = Math.floor(differenceInDays / 30);
      const differenceInYears = Math.floor(differenceInDays / 365.25);

      if (differenceInMinutes < 60) {
         return `${differenceInMinutes} minute${differenceInMinutes === 1 ? '' : 's'} ago`;
      } else if (differenceInHours < 24) {
         return `${differenceInHours} hour${differenceInHours === 1 ? '' : 's'} ago`;
      } else if (differenceInDays < 30) {
         return `${differenceInDays} day${differenceInDays === 1 ? '' : 's'} ago`;
      } else if (differenceInMonths < 12) {
         return `${differenceInMonths} Month${differenceInMonths === 1 ? '' : 's'} ago`;
      }
      return `${differenceInYears} Year${differenceInYears === 1 ? '' : 's'} ago`;

   }

   return (
      <>
         {
            loading && allComments.length === 0
               ? <Loading />
               : error
                  ? (
                     <Box display="flex" alignItems="center" gap={1} color="error" p={4}>
                        <ErrorIcon fontSize="large" color='error' />
                        <Typography variant="h4" color='red'>error while loading comments and worknotes!</Typography>
                     </Box>
                  )
                  :
                  allComments && allComments.length > 0 && (
                     allComments.map((item: { value: string; createdOn: string; type: string; createdBy: string }, index: number) => (
                        <CommentEntry
                           key={index}
                           item={item}
                           getTimeDifference={getTimeDifference}
                        />
                     ))
                  )
         }
         {!endOfComments && !loading && !error && allComments.length > 0 &&
            <div className='col-12 col-md-10 col-lg-12 pt-3 d-flex justify-content-center show-more-div'>
               <button onClick={nextComments} className='Show-more btn btn-light'>Show More</button>
            </div>}
      </>
   )
}

type CommentEntryProps = {
   item: { value: string; createdOn: string; type: string; createdBy: string };
   getTimeDifference: (createdOn: string) => string;
}

// Split out so useInlineAttachmentImages (a hook) can be called once per comment,
// resolving any ServiceNow inline ".iix" comment images to data: URLs before render.
const CommentEntry = ({ item, getTimeDifference }: CommentEntryProps) => {
   const { isUserAllowedtoDownloadAttachments } = useContext(PermissionContext);
   const sanitizedValue = DOMPurify.sanitize(item.value);
   const { resolvedHtml } = useInlineAttachmentImages(sanitizedValue, isUserAllowedtoDownloadAttachments);

   return (
      <div className='pt-4'>
         <div className="col-12 col-md-10 col-lg-12 ">
            <div className={item.type === "comments" ? "card card-sm reply-box" : "card card-sm reply-box work-note-card"}>
               <span className='p-3'>
                  {item.type === "comments" && <img src={Clock} alt="Logo" className='pe-2' />}
                  {item.type === "work_notes" && <img src={Note} alt="Logo" className='pe-2' />}
                  {new Date(item.createdOn + "Z").toLocaleString()} <span style={{ marginLeft: '8px', }}>({getTimeDifference(item.createdOn)})</span>
                  <span className='created-by'>{item.createdBy}</span>
               </span>
               <div className="card-body row no-gutters align-items-center">
                  <div className="overflow-auto"><div className="comment-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolvedHtml) }}></div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
}
